import {FC} from 'react'

import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {TextStyleByDevice, TextVariant} from './Text'

/**
 * ContentWithInnerHTML
 *
 * Use this component for marketing page content generated from Google Sheets or Contentful (Trademark Q&As, Trademark dictionary, Specimen guide, ...).
 * It applies specific spacing and typography rules tailored for marketing layouts.
 * The `textVariant` prop can customize the text style, but margins are optimized for these pages.
 *
 */

type ContentWithInnerHTMLProps = {
  content: string
  textVariant?: TextVariant
}

export const ContentWithInnerHTML: FC<React.PropsWithChildren<ContentWithInnerHTMLProps>> = ({
  content,
  textVariant = TextVariant.text20,
}) => {
  const paragraphStyles = theme.typography[textVariant] as TextStyleByDevice
  const paragraphDesktopStyles = paragraphStyles.desktop
  const paragraphMobileStyles = paragraphStyles?.mobile

  return (
    <>
      <div className="content-inner-html" dangerouslySetInnerHTML={{__html: content}} />

      <style jsx global>{`
        .content-inner-html {
          color: ${theme.palette.text.default};
        }
        .content-inner-html h2 {
          margin: 0 0 ${theme.spacing[5]};
          ${theme.typography.h3.desktop};
        }
        .content-inner-html h2:not(:first-of-type) {
          margin-top: ${theme.spacing[7]};
        }
        .content-inner-html h3 {
          margin: ${theme.spacing[5]} 0 ${theme.spacing[3]};
          ${theme.typography.h5.desktop};
        }
        .content-inner-html p,
        .content-inner-html li {
          margin: 0;
          ${paragraphDesktopStyles};
        }
        .content-inner-html p + p {
          margin-top: ${theme.spacing[5]};
        }
        .content-inner-html p + .content-inner-html ul,
        .content-inner-html p + .content-inner-html ol {
          margin-top: ${theme.spacing[5]};
        }
        .content-inner-html ul {
          list-style-type: disc;
          margin-left: ${theme.spacing[4]};
        }
        .content-inner-html ol {
          list-style-type: decimal;
          margin-left: ${theme.spacing[4]};
        }
        .content-inner-html li ul {
          list-style-type: circle;
        }
        .content-inner-html li + li {
          margin-top: ${theme.spacing[2.5]};
        }
        .content-inner-html ul + p,
        .content-inner-html ol + p {
          margin-top: ${theme.spacing[5]};
        }
        .content-inner-html p + ul,
        .content-inner-html p + ol {
          margin-top: ${theme.spacing[4]};
        }
        .content-inner-html a {
          text-decoration: underline;
          overflow-wrap: anywhere;
        }
        @media ${MOBILE_SCREENSIZE} {
          .content-inner-html h2 {
            margin: 0 0 ${theme.spacing[4]};
            ${theme.typography.h3.mobile};
          }
          .content-inner-html h3 {
            ${theme.typography.h5.mobile};
          }
          .content-inner-html p,
          .content-inner-html li {
            ${paragraphMobileStyles};
          }
          .content-inner-html p + p {
            margin-top: ${theme.spacing[4]};
          }
          .content-inner-html ul,
          .content-inner-html ol {
            margin-top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[3]};
          }
          .content-inner-html ul + p,
          .content-inner-html ol + p,
          .content-inner-html p + ul,
          .content-inner-html p + ol {
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
